import { Badge, Button, Drawer, Group, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconEdit } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import type { FeePolicy } from "../../apis/tenantFeePolicy";
import { formatMoney, formatPercent } from "../../utils/format";
import {
  calcMethodLabel,
  deductionModeLabel,
  feeBasisLabel,
  refundPolicyLabel,
  scopeLabel,
} from "../../utils/fee-policy-labels";
import { FeePolicyStatusBadge } from "./FeePolicyStatusBadge";

interface Props {
  policy: FeePolicy | null;
  opened: boolean;
  onClose: () => void;
  /** When true, hides the Edit button (e.g., platform admin read-only view). */
  hideEdit?: boolean;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>{value}</Text>
    </Stack>
  );
}

const SCOPE_COLOR: Record<string, string> = { DEFAULT: "violet", NAMED: "cyan" };

export function PolicyReadOnlyDrawer({ policy, opened, onClose, hideEdit }: Props) {
  const navigate = useNavigate();
  if (!policy) {
    return <Drawer opened={opened} onClose={onClose} position="right" size="lg" title="Бодлого" />;
  }

  const methodLine =
    policy.calculationMethod === "PERCENTAGE" && policy.feeRate
      ? `${calcMethodLabel("PERCENTAGE")} · ${formatPercent(Number(policy.feeRate) * 100)}`
      : policy.calculationMethod === "FLAT" && policy.flatFeeAmount
      ? `${calcMethodLabel("FLAT")} · ${formatMoney(policy.flatFeeAmount, policy.feeCurrency)}`
      : calcMethodLabel(policy.calculationMethod);

  const onEdit = () => {
    onClose();
    navigate(`/my-billing/fee-policy/editor/${policy.id}`);
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        <Group gap="xs">
          {policy.scope && (
            <Badge color={SCOPE_COLOR[policy.scope] ?? "gray"}>{scopeLabel(policy.scope)}</Badge>
          )}
          <FeePolicyStatusBadge status={policy.status} />
          <Text fw={600}>{policy.name ?? (policy.scope === "DEFAULT" ? "Үндсэн" : "(нэргүй)")}</Text>
        </Group>
      }>
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Row label="Тооцоолох арга" value={methodLine} />
          <Row
            label="Min / Max"
            value={`${policy.minFeeAmount ? formatMoney(policy.minFeeAmount, policy.feeCurrency) : "—"} / ${
              policy.maxFeeAmount ? formatMoney(policy.maxFeeAmount, policy.feeCurrency) : "—"
            }`}
          />
          <Row label="Тооцох суурь" value={feeBasisLabel(policy.feeBasis)} />
          <Row label="Хасах арга" value={deductionModeLabel(policy.deductionMode)} />
          <Row label="Буцаалт" value={refundPolicyLabel(policy.refundPolicy)} />
          <Row label="Платформын fee хасах" value={policy.absorbPlatformFee ? "Тийм" : "Үгүй"} />
        </SimpleGrid>

        {!hideEdit && (
          <Group justify="flex-end">
            <Button leftSection={<IconEdit size={16} />} onClick={onEdit}>
              Засах
            </Button>
          </Group>
        )}
      </Stack>
    </Drawer>
  );
}
